/**
 * HelpPage — Help & Support page with issue submission and FAQ
 *
 * Features:
 * - Issue type dropdown (common issues + feature request)
 * - Description textarea for details
 * - Submit button
 * - FAQ accordion section (collapse/expand)
 *
 * API_TODO:
 * - POST /api/help/submit { type, description }
 * - GET /api/help/faq → fetch FAQ items from backend
 */
import { useState } from 'react';
import { Icons } from '../components/Icons';
import { useHelp } from '../context/HelpContext';

const ISSUE_TYPES = [
  { value: '', label: 'Select an issue...' },
  { value: 'profile_not_saving', label: 'Profile changes not saving' },
  { value: 'qr_not_scanning', label: 'QR code not scanning properly' },
  { value: 'wallet_pass', label: 'Wallet pass not working (Apple/Google)' },
  { value: 'photo_upload', label: 'Photo upload failing' },
  { value: 'scan_notifications', label: 'Not receiving scan notifications' },
  { value: 'links_broken', label: 'Links not opening correctly' },
  { value: 'account_access', label: 'Cannot access my account' },
  { value: 'slow_loading', label: 'App loading slowly' },
  { value: 'other_issue', label: 'Other issue' },
  { value: 'feature_request', label: 'New Feature Request' },
];

const FAQ_ITEMS = [
  {
    question: 'How do I share my QR card?',
    answer: 'Go to "Your QR Card" in the sidebar. You can download the QR image, add it to Apple Wallet or Google Wallet, or share it directly via social media. You can also print it on business cards or display it at events.',
  },
  {
    question: 'Why is my QR code not scanning?',
    answer: 'Ensure your QR code has enough contrast and is displayed at a reasonable size (at least 2cm x 2cm). If the issue persists, try regenerating your QR card from the profile editor. Some older phone cameras may have trouble with halftone-style QR codes — the standard QR option is more universally compatible.',
  },
  {
    question: 'How do I edit my profile?',
    answer: 'Navigate to "Profile Editor" from the sidebar. You can update your name, title, company, tagline, about section, and links. Don\'t forget to click "Save & Publish" to apply your changes.',
  },
  {
    question: 'Who can see my profile when they scan my QR code?',
    answer: 'Anyone who scans your QR code will see the information you\'ve made visible in your profile editor. You can toggle the visibility of individual links and contact details using the eye icon next to each field.',
  },
  {
    question: 'How does the referral system work?',
    answer: 'Go to the "Referrals" tab to get your unique referral link. When someone signs up using your link, both of you earn points. Points can be redeemed for premium features in the future.',
  },
  {
    question: 'Can I have multiple QR cards?',
    answer: 'Currently, each account supports one profile and one QR card. If you need separate cards for different roles, you can create additional accounts with different email addresses.',
  },
  {
    question: 'How do I add my QR to Apple/Google Wallet?',
    answer: 'On the "Your QR Card" page, click the "Add to Apple Wallet" or "Add to Google Wallet" button. This creates a digital pass on your phone that you can quickly show at events without opening the app.',
  },
  {
    question: 'Is Connekted in free?',
    answer: 'Yes! Connekted in is completely free to use. We believe everyone deserves a modern digital business card. Premium features with advanced analytics and customisation may be offered in the future.',
  },
];

export default function HelpPage({ user }) {
  const { helpTickets, featureRequests, submitTicket } = useHelp();
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Get tickets submitted by this user
  const myTickets = [
    ...helpTickets.filter(t => t.user === user.name),
    ...featureRequests.filter(t => t.user === user.name),
  ].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

  const handleSubmit = () => {
    if (!issueType || !description.trim()) return;

    const submission = {
      id: `h${Date.now()}`,
      type: issueType,
      typeLabel: ISSUE_TYPES.find(t => t.value === issueType)?.label || issueType,
      description: description.trim(),
      submittedAt: new Date().toISOString(),
      status: 'open',
      user: user.name,
      adminResponse: '',
    };

    // Submit to shared context — appears in admin panel immediately
    submitTicket(submission);

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setIssueType('');
      setDescription('');
    }, 3000);
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <>
      <div className="page-header">
        <div className="mono" style={{ marginBottom: 8 }}>Help & Support</div>
        <h1 className="page-title">How can we <em>help</em>?</h1>
        <p className="page-subtitle">Submit an issue or browse our FAQ below.</p>
      </div>

      <div className="page-body">
        {/* ── Submit Issue Section ── */}
        <div className="panel">
          <div className="panel-label">Report an Issue</div>

          <div className="help-form">
            <div className="help-field">
              <label className="help-label">Issue Type</label>
              <div className="help-select-wrap">
                <select
                  className="help-select"
                  value={issueType}
                  onChange={e => setIssueType(e.target.value)}
                >
                  {ISSUE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <span className="help-select-chevron">{Icons.chevron}</span>
              </div>
            </div>

            <div className="help-field">
              <label className="help-label">
                {issueType === 'feature_request' ? 'Describe your feature idea' : 'Describe the issue'}
              </label>
              <textarea
                className="help-textarea"
                placeholder={
                  issueType === 'feature_request'
                    ? 'Tell us about the feature you\'d like to see...'
                    : 'Please describe what happened, when it occurred, and any steps to reproduce...'
                }
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={5}
              />
            </div>

            <button
              className="btn btn-primary-app"
              onClick={handleSubmit}
              disabled={!issueType || !description.trim() || submitted}
              style={{ alignSelf: 'flex-start' }}
            >
              {submitted ? (
                <>{Icons.check} Submitted!</>
              ) : issueType === 'feature_request' ? (
                'Submit Feature Request'
              ) : (
                'Submit Issue'
              )}
            </button>

            {submitted && (
              <p className="help-success">
                Thank you! Your {issueType === 'feature_request' ? 'feature request' : 'issue report'} has been received. We'll get back to you soon.
              </p>
            )}
          </div>
        </div>

        {/* ── Your Submissions Tracker ── */}
        {myTickets.length > 0 && (
          <div className="panel" style={{ marginTop: 24 }}>
            <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Your Submissions
              <span style={{
                background: 'var(--brand)', color: 'white', fontSize: 11,
                padding: '1px 8px', borderRadius: 10, fontWeight: 600
              }}>
                {myTickets.length}
              </span>
            </div>
            <div className="tracker-list">
              {myTickets.map(ticket => {
                const isFeatureRequest = ticket.type === 'feature_request';
                const finalStatus = isFeatureRequest ? 'dismissed' : 'resolved';
                const finalLabel = isFeatureRequest ? 'Reviewed' : 'Resolved';
                const midActive = ['in_progress', finalStatus].includes(ticket.status);
                const endActive = ticket.status === finalStatus;

                return (
                  <div key={ticket.id} className="tracker-card">
                    {/* Status progress bar */}
                    <div className="tracker-progress">
                      <div className={`tracker-step ${['open', 'in_progress', finalStatus].includes(ticket.status) ? 'active' : ''}`}>
                        <div className="tracker-dot" />
                        <span>Open</span>
                      </div>
                      <div className={`tracker-line ${midActive ? 'active' : ''}`} />
                      <div className={`tracker-step ${midActive ? 'active' : ''}`}>
                        <div className="tracker-dot" />
                        <span>In Progress</span>
                      </div>
                      <div className={`tracker-line ${endActive ? 'active' : ''}`} />
                      <div className={`tracker-step ${endActive ? 'active' : ''}`}>
                        <div className="tracker-dot" />
                        <span>{finalLabel}</span>
                      </div>
                    </div>

                    {/* Ticket info */}
                    <div className="tracker-info">
                      <div className="tracker-type">{ticket.typeLabel}</div>
                      <p className="tracker-desc">{ticket.description}</p>
                      <div className="tracker-meta mono">
                        Submitted {new Date(ticket.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>

                    {/* Admin response (if any) */}
                    {ticket.adminResponse && (
                      <div className="tracker-response">
                        <div className="tracker-response-label">
                          {Icons.bell} Team Response
                        </div>
                        <p className="tracker-response-text">{ticket.adminResponse}</p>
                        {ticket.respondedAt && (
                          <div className="tracker-meta mono" style={{ marginTop: 4 }}>
                            {new Date(ticket.respondedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── FAQ Section ── */}
        <div className="panel" style={{ marginTop: 24 }}>
          <div className="panel-label">Frequently Asked Questions</div>

          <div className="faq-list">
            {FAQ_ITEMS.map((item, index) => (
              <div key={index} className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{item.question}</span>
                  <span className={`faq-chevron ${expandedFaq === index ? 'rotated' : ''}`}>
                    {Icons.chevron}
                  </span>
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
